import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, MapPin, Award, Gavel, CalendarDays, Rocket, ArrowRight, Briefcase, Mail, MessageSquare, Share2, Users, Ticket, Megaphone, ChevronDown } from 'lucide-react';

const STEPS = [
  {
    icon: Zap, title: 'Earn cash rewards by growing the business community', featured: true, cta: { to: '/blackout/info', label: 'Play the Blackout Game' },
    body: (
      <>
        Play the <b>Blackout Game</b> to add Black-owned businesses and <b>events</b> that aren't on the app yet. When a business you discovered
        upgrades to a paid plan, you earn <b>3% of its subscription payments</b> plus a 100-point bonus. When an event you added sells tickets,
        you earn <b>1% of every ticket sold</b>. Your cash rewards build up in your earnings balance and unlock on April 1, 2027.
      </>
    ),
  },
  {
    icon: MapPin, title: 'Find businesses in the Directory and on the Golden Map', cta: { to: '/directory', label: 'Open the Directory' },
    body: <>Search the Directory by category or city, or explore the Golden Map to see who's near you. A gold <b>$</b> marks active paid members, and a dot marks new discoveries.</>,
  },
  {
    icon: Award, title: 'Earn points by engaging', cta: { to: '/news', label: 'Read the News Feed' },
    body: <>Earn PBB points when you shop, check in, use loyalty cards and interact with local businesses. You can also read the News Feed and answer a quick question to earn 10 points per article, up to 3 articles a day.</>,
  },
  {
    icon: Gavel, title: 'Use your points in the Auction Store and on coupons', cta: { to: '/auction', label: 'Visit the Auction Store' },
    body: <>Bid with your points on donated items in the C.A.N. (Community Auction Network) Auction Store. Grab coupons from local businesses to save when you shop.</>,
  },
  {
    icon: CalendarDays, title: 'Check the Events calendar and the Leaderboard', cta: { to: '/events', label: 'See upcoming events' },
    body: <>Find community events, buy tickets, and show up for local owners. Then check the Leaderboard to see how your points stack up against other members.</>,
  },
  {
    icon: Rocket, title: 'Be part of the pilot',
    body: <>You're an early member of our pilot. Revenue from subscriptions and sales goes into the <b>capital fund</b>, which is advancing the pilot's success and the official launch of the app in the App Store in April 2027.</>,
  },
];

const OWNER_TOOLS = [
  [Users, 'Customer contacts & CRM', 'Every opt-in and sign-up lands in one contact list you can sort, tag and export.'],
  [Mail, 'Email & SMS campaigns', 'Send AI-written emails and text messages, plus automatic welcome, birthday and win-back messages.'],
  [Share2, 'Landing pages for your social media', 'Build a landing page with a sign-up form and share the link on Facebook, Instagram and anywhere else your customers follow you.'],
  [Ticket, 'Coupons, loyalty cards & events', 'Reward repeat customers, run promotions and sell event tickets right inside the app.'],
  [MessageSquare, 'Surveys & customer insights', 'Ask customers what they want with AI-built surveys and see who is engaging with your business.'],
  [Megaphone, 'Directory, Golden Map & homepage slider', 'Get found by members in the Directory and on the Golden Map. Full Package members can also appear in the homepage banner slider.'],
];

const StepCard = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <li className={`rounded-2xl p-6 md:p-7 flex gap-5 ${s.featured ? 'bg-neutral-950 text-white ring-2 ring-yellow-400 shadow-xl' : 'bg-white text-neutral-900 border border-neutral-200 shadow-sm'}`} data-testid={`hiw-step-${i + 1}`}>
      <div className="shrink-0 flex flex-col items-center gap-2">
        <span className={`w-11 h-11 rounded-full flex items-center justify-center ${s.featured ? 'bg-yellow-400 text-black' : 'bg-green-700 text-white'}`}><Icon size={20}/></span>
        <span className={`text-xs font-bold ${s.featured ? 'text-yellow-300' : 'text-neutral-500'}`}>STEP {i + 1}</span>
      </div>
      <div className="min-w-0">
        {s.featured && <div className="text-[11px] uppercase tracking-widest font-bold text-yellow-300 mb-1">Start here</div>}
        <h3 className={`font-bold ${s.featured ? 'text-xl md:text-2xl' : 'text-lg'}`}>{s.title}</h3>
        <p className={`mt-2 leading-relaxed ${s.featured ? 'text-neutral-100 text-base' : 'text-neutral-700 text-sm md:text-base'}`}>{s.body}</p>
        {s.cta && (
          <Link to={s.cta.to} className={`mt-3 inline-flex items-center gap-1 text-sm font-semibold ${s.featured ? 'text-yellow-300 hover:text-yellow-200' : 'text-green-800 hover:text-green-700'}`} data-testid={`hiw-step-${i + 1}-link`}>
            {s.cta.label} <ArrowRight size={14}/>
          </Link>
        )}
      </div>
    </li>
  );
};

const GetStarted = ({ testId, dark }) => (
  <Link to="/signup" data-testid={testId}
    className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-base transition-transform hover:-translate-y-0.5 ${dark ? 'bg-yellow-400 text-black hover:bg-yellow-300' : 'bg-green-700 text-white hover:bg-green-600'}`}>
    Get Started <ArrowRight size={18}/>
  </Link>
);

export default function HowItWorks() {
  return (
    <div className="bg-neutral-50 text-neutral-900" data-testid="how-it-works-page">
      <section className="bg-neutral-950 text-white">
        <div className="max-w-5xl mx-auto px-5 py-16 md:py-20 grid md:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-green-400 font-bold">How It Works</div>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">Together, we build our economy.</h1>
            <p className="mt-5 text-lg text-neutral-100 leading-relaxed" data-testid="hiw-purpose">
              Promote Black Business is a system that partners consumers and business owners to work together, funding the
              infrastructure we need to strengthen our economy right where we live.
            </p>
            <a href="#business-owners" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-yellow-300 hover:text-yellow-200 underline underline-offset-4" data-testid="hiw-owner-jump">
              Are you a business owner? Jump to your section <ChevronDown size={14}/>
            </a>
          </div>
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/welcome-consumer.jpg`} alt="Community members celebrating local Black-owned businesses" className="w-full rounded-2xl shadow-2xl ring-1 ring-white/10 object-cover aspect-[3/2]"/>
        </div>
      </section>

      <section className="bg-green-800 text-white">
        <div className="max-w-5xl mx-auto px-5 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <h2 className="text-2xl md:text-3xl font-black leading-snug" data-testid="hiw-cash-headline">Earn cash rewards by helping your community grow.</h2>
          <GetStarted testId="hiw-get-started-top" dark/>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 py-14" data-testid="hiw-shoppers">
        <div className="text-xs uppercase tracking-widest text-green-800 font-bold">For shoppers & members</div>
        <h2 className="mt-2 text-3xl md:text-4xl font-black">Six simple steps</h2>
        <ol className="mt-8 space-y-5">{STEPS.map((s, i) => <StepCard key={s.title} s={s} i={i}/>)}</ol>
        <div className="mt-10 rounded-2xl bg-white border border-neutral-200 p-7 text-center shadow-sm">
          <p className="text-lg font-semibold text-neutral-900">Ready to discover, earn and grow with us?</p>
          <div className="mt-4"><GetStarted testId="hiw-get-started-bottom"/></div>
        </div>
      </section>

      <section id="business-owners" className="bg-neutral-950 text-white scroll-mt-20" data-testid="hiw-owners">
        <div className="max-w-5xl mx-auto px-5 py-16 grid md:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div>
            <div className="text-xs uppercase tracking-widest text-yellow-300 font-bold inline-flex items-center gap-1.5"><Briefcase size={13}/> For business owners</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-black">Welcome, business owner. We built this for you.</h2>
            <p className="mt-4 text-neutral-100 leading-relaxed">
              You're busy running your business, and the most popular marketing tools take time and know-how to use. We put
              those tools in one place, made them simple, and connected them to a community that wants to support you.
            </p>
            <p className="mt-3 text-neutral-100 leading-relaxed">
              Reach customers inside the app, and also directly through your own social media, email and text messages.
            </p>
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/welcome-owner.jpg`} alt="A business owner celebrating a grand opening" className="mt-6 w-full rounded-2xl ring-1 ring-white/10 object-cover aspect-[3/2]"/>
            <Link to="/plans" data-testid="hiw-choose-plan"
              className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold transition-transform hover:-translate-y-0.5">
              Choose a Plan <ArrowRight size={18}/>
            </Link>
          </div>
          <ul className="grid sm:grid-cols-2 gap-4">
            {OWNER_TOOLS.map(([Icon, t, d]) => (
              <li key={t} className="rounded-xl bg-neutral-900 border border-neutral-800 p-5" data-testid="hiw-owner-tool">
                <Icon size={20} className="text-yellow-300"/>
                <div className="mt-2 font-bold text-white">{t}</div>
                <p className="mt-1 text-sm text-neutral-200 leading-relaxed">{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
